var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useMemo, useRef, useState } from 'react';
import { useApolloClient, useLazyQuery } from '@apollo/client/react';
import { t } from '@lingui/core/macro';
import { useToast } from '@pocketlaw/tetris';
import { cleanValue, downloadBlob, stringifyCellValue } from 'app/domains/download/utils/csv';
import { useSearchContext } from 'app/domains/global-search';
import { SearchEntityTypeEnum } from 'shared/domains/apollo/generated/types';
import { GlobalSearchDocument } from '../../hooks/useSearchQuery/GlobalSearch.gql';
import { GlobalSearchUsersQueryFragmentDoc } from '../../hooks/useSearchQuery/GlobalSearchUsersQuery.gql';
const DELIMITER = ',';
/**
 * A custom React hook for downloading CSV data from a global search.
 * This hook manages the state for downloading large datasets in batches,
 */
export function useDownloadCSV() {
    const [currentBatchIndex, setCurrentBatchIndex] = useState(0);
    const [maxBatchIndex, setMaxBatchIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const abortControllerRef = useRef(null);
    const [fetchSearch, { error }] = useLazyQuery(GlobalSearchDocument);
    const { values } = useSearchContext();
    const { addToast } = useToast();
    const client = useApolloClient();
    const { columns, query, filters, orderBy } = values;
    const visibleColumns = useMemo(() => columns.filter((col) => col.active && col.id !== 'action'), [columns]);
    const cancel = useCallback(() => {
        var _a;
        (_a = abortControllerRef.current) === null || _a === void 0 ? void 0 : _a.abort();
    }, []);
    const download = useCallback((options) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        const { prettifyValues } = options;
        // We assume company users are queried by the search.
        const cachedUsers = client.cache.readFragment({
            fragment: GlobalSearchUsersQueryFragmentDoc,
        });
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const limit = 500;
        let offset = 0;
        let csvContent = '';
        const headers = `${visibleColumns.map((column) => cleanValue(column.label || '')).join(DELIMITER)}\n`;
        csvContent += headers;
        try {
            setLoading(true);
            // eslint-disable-next-line no-constant-condition
            while (true) {
                setCurrentBatchIndex((prevIndex) => prevIndex + 1);
                // eslint-disable-next-line no-await-in-loop
                const { data } = yield fetchSearch({
                    fetchPolicy: 'network-only',
                    context: { fetchOptions: { signal: controller.signal } },
                    variables: {
                        query,
                        where: Object.assign(Object.assign({}, filters), { entityType: {
                                in: [
                                    SearchEntityTypeEnum.DocumentComposed,
                                    SearchEntityTypeEnum.DocumentFreetext,
                                    SearchEntityTypeEnum.DocumentUploaded,
                                ],
                            } }),
                        orderBy,
                        limit,
                        offset,
                    },
                });
                if (!((_a = data === null || data === void 0 ? void 0 : data.search) === null || _a === void 0 ? void 0 : _a.results) || data.search.results.length === 0) {
                    break;
                }
                if (currentBatchIndex === 0) {
                    const totalHitsCount = data.search.totalHitsCount || 0;
                    setMaxBatchIndex(Math.ceil(totalHitsCount / limit));
                }
                const batchContent = data.search.results
                    .map((row) => visibleColumns
                    .map((column) => {
                    switch (column.id) {
                        // NOTE: We're currently ignoring the custom metadata user references.
                        case 'createdByUser':
                        case 'metadataDocumentContractOwner':
                        case 'metadataProjectMembersCombinedUserIds':
                        case 'metadataProjectProjectOwner':
                        // eslint-disable-next-line padding-line-between-statements, no-fallthrough
                        case 'metadataProjectMembersUserIds': {
                            const rawValue = row[column.id];
                            const valueArray = Array.isArray(rawValue) ? rawValue : [rawValue];
                            if (prettifyValues) {
                                const users = valueArray.map((userId) => {
                                    var _a;
                                    const { name } = ((_a = cachedUsers === null || cachedUsers === void 0 ? void 0 : cachedUsers.users) === null || _a === void 0 ? void 0 : _a.find((ch) => ch.id === userId)) || {};
                                    return name || userId;
                                });
                                return users.join(', ');
                            }
                            return valueArray.join(', ');
                        }
                        default:
                            return stringifyCellValue(row[column.id]);
                    }
                })
                    .map(cleanValue)
                    .join(DELIMITER))
                    .join('\n');
                csvContent += `${batchContent}\n`;
                if (offset >= 10000 || data.search.results.length < limit) {
                    break;
                }
                offset += limit;
            }
            if (!controller.signal.aborted) {
                downloadBlob(csvContent);
                addToast({
                    title: t({
                        message: 'Download complete',
                        comment: 'CSV download button: Download complete toast',
                    }),
                    appearance: 'success',
                });
            }
        }
        catch (err) {
            if (err instanceof Error && err.name === 'AbortError') {
                return;
            }
            addToast({
                title: t({
                    message: 'Download failed',
                    comment: 'CSV download button: Download failed toast',
                }),
                appearance: 'danger',
            });
        }
        finally {
            setLoading(false);
            setCurrentBatchIndex(0);
            setMaxBatchIndex(0);
        }
    }), [client.cache, visibleColumns, fetchSearch, query, filters, orderBy, currentBatchIndex, addToast]);
    const progress = currentBatchIndex && maxBatchIndex ? Math.round((currentBatchIndex / maxBatchIndex) * 100) : 0;
    return { download, cancel, loading, error, progress };
}
